/**
 * Архив: что платформа уже провела.
 *
 * Лента показывает то, что идёт сейчас, и история в неё не помещается. А
 * история — это и есть главное доказательство платформы: вот проведённые
 * акции, у каждой опубликован сид, список билетов и победители, и любой
 * пересчёт можно повторить. Бренду, который решает, идти ли сюда, этот
 * список говорит больше, чем описание продукта.
 *
 * Поэтому архив — витрина проведённого, а не свалка завершённого: годы,
 * числа и ссылка на отчёт розыгрыша у каждой акции.
 */
import { Badge, BrandMark, Card, Empty, Icon, Skeleton, fmtDay, num, regionName } from '@allswipes/ui';
import { useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { api } from '../api';
import { usePlural, useT } from '@allswipes/i18n';
import { useReveal } from '../anim';
import { Shell } from '../components/Shell';
import { useResource } from '../hooks';
import { useSession } from '../session';

export function ArchiveScreen() {
  const plural = usePlural();
  const t = useT();
  const { signedIn } = useSession();
  // Фильтры живут в адресе: ссылкой на «Казахстан, весна 2026» можно
  // поделиться, и «назад» возвращает прошлый выбор, а не всю ленту заново.
  const [params, setParams] = useSearchParams();
  const region = params.get('region') ?? '';
  const from = params.get('from') ?? '';
  const to = params.get('to') ?? '';

  const data = useResource(
    () => api.archive({ region, from, to }),
    [region, from, to, signedIn],
  );
  const root = useRef<HTMLDivElement>(null);
  useReveal(root, [data.data], { selector: '.arow' });

  const view = data.data;

  const setFilter = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  return (
    <Shell
      wide
      title={t('Архив')}
      subtitle={t('акции, которые уже прошли: сид опубликован, результат пересчитывается')}
    >
      {data.loading && <Skeleton count={4} height={72} />}

      {view && view.totals.campaigns === 0 && !region && !from && !to && (
        <Empty icon="scroll" title={t('Архив пока пуст')}>
          {t('Сюда попадают акции, которые закончились. Первая появится здесь, как только завершится хотя бы одна.')}
        </Empty>
      )}

      {view && (view.totals.campaigns > 0 || region || from || to) && (
        <div className="afilters">
          <div className="chips">
            <button
              type="button"
              className={`chip ${region ? '' : 'on'}`}
              onClick={() => setFilter('region', '')}
            >
              {t('Все страны')} <i>{view.regions.reduce((sum, r) => sum + r.count, 0)}</i>
            </button>
            {view.regions.map((r) => (
              <button
                key={r.code}
                type="button"
                className={`chip ${region === r.code ? 'on' : ''}`}
                onClick={() => setFilter('region', region === r.code ? '' : r.code)}
              >
                {t(regionName(r.code))} <i>{r.count}</i>
              </button>
            ))}
          </div>

          <div className="adates">
            <label>
              <span>{t('с')}</span>
              <input
                type="date"
                value={from}
                min={view.range.from}
                max={to || view.range.to}
                onChange={(e) => setFilter('from', e.target.value)}
              />
            </label>
            <label>
              <span>{t('по')}</span>
              <input
                type="date"
                value={to}
                min={from || view.range.from}
                max={view.range.to}
                onChange={(e) => setFilter('to', e.target.value)}
              />
            </label>
            {(from || to || region) && (
              <button type="button" className="aclear" onClick={() => setParams({}, { replace: true })}>
                <Icon name="close" size={12} />
                {t('Сбросить')}
              </button>
            )}
          </div>
        </div>
      )}

      {view && view.totals.campaigns === 0 && (region || from || to) && (
        <Empty icon="search" title={t('В этом промежутке ничего нет')}>
          {t('Попробуй другую страну или раздвинь даты.')}
        </Empty>
      )}

      {view && view.totals.campaigns > 0 && (
        <>
          <div className="stats" style={{ marginBottom: 20 }}>
            <Stat value={num(view.totals.campaigns)} label={t('акций проведено')} />
            <Stat value={num(view.totals.participants)} label={t('участников')} />
            <Stat value={num(view.totals.prizes)} label={t('призовых мест')} />
          </div>

          <div ref={root}>
            {view.years.map((year) => (
              <section key={year.year} style={{ marginBottom: 26 }}>
                <div className="sechead">
                  <span className="t">{year.year}</span>
                  <span className="n">
                    {num(year.totals.campaigns)}{' '}
                    {t(plural(year.totals.campaigns, t('акция'), t('акции'), t('акций')))} ·{' '}
                    {num(year.totals.participants)} {t('участников')}
                  </span>
                </div>

                <Card>
                  {year.campaigns.map((card) => (
                    <div className="arow" key={card.campaign.id}>
                      <BrandMark
                        name={card.brand?.name ?? '—'}
                        logoUrl={card.brand?.logo_url}
                        size={28}
                      />

                      <div style={{ minWidth: 0 }}>
                        <div className="row" style={{ gap: 8 }}>
                          <Link to={`/c/${card.campaign.id}`} className="t">
                            {card.campaign.prize_title || card.campaign.name}
                          </Link>
                          {card.campaign.status === 'drawn' && <Badge tone="good">{t('розыгрыш')}</Badge>}
                        </div>
                        <div className="meta">
                          <span>{card.brand?.name}</span>
                          <span>{fmtDay(card.ended_at)}</span>
                          <span>
                            {num(card.campaign.participants)}{' '}
                            {t(plural(card.campaign.participants, t('участник'), t('участника'), t('участников')))}
                          </span>
                          {card.winners > 0 && (
                            <span>
                              {num(card.winners)}{' '}
                              {t(plural(card.winners, t('приз'), t('приза'), t('призов')))}
                            </span>
                          )}
                        </div>
                      </div>

                      <span className="spacer" />

                      {card.campaign.status === 'drawn' ? (
                        <Link to={`/draw/${card.campaign.id}`} className="btn out sm">
                          <Icon name="shield" size={13} />
                          {t('Отчёт розыгрыша')}
                        </Link>
                      ) : (
                        <Link to={`/c/${card.campaign.id}`} className="btn out sm">
                          {t('Страница акции')}
                        </Link>
                      )}
                    </div>
                  ))}
                </Card>
              </section>
            ))}
          </div>

          {view.limited && (
            <Card className="alock">
              <Icon name="scroll" size={18} />
              <div style={{ minWidth: 0 }}>
                <div className="t">
                  {t('Показано')} {num(view.shown)} {t('из')} {num(view.totals.campaigns)}{' '}
                  {t(plural(view.totals.campaigns, t('акции'), t('акций'), t('акций')))}
                </div>
                <p className="sub">
                  {t('Весь архив открывается после входа — это тот же вход, что для участия, по коду из письма. Страница любой акции и отчёт розыгрыша при этом открыты всем и без него: доказательство прятать нельзя.')}
                </p>
              </div>
              <span className="spacer" />
              <Link to="/login" className="btn pri">
                {t('Войти')}
              </Link>
            </Card>
          )}
        </>
      )}
    </Shell>
  );
}

function Stat({ value, label }: { value: string; label: string }) {
  return (
    <div className="s">
      <div className="sv">{value}</div>
      <div className="sl">{label}</div>
    </div>
  );
}
